import { useEffect, useMemo, useState } from "react";
import { TablePager } from "../components/TablePager";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { useTabelIndeling } from "../hooks/useTabelIndeling";
import {
  DateField,
  Button,
  DataTable,
  FormCard,
  SearchIcon,
  SectionHeader,
  StatusBadge,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatDate, formatEuro } from "../i18n/format";

interface FactuurListItem {
  id: number;
  nummer: string;
  factuurType: string | null;
  factuurDatum: string | null;
  vervalDatum: string | null;
  status: "CONCEPT" | "BETAALD" | "OPENSTAAND" | "VERVALLEN";
  klantId: number | null;
  klantNaam: string | null;
  totaal: number | null;
  betaald: number | null;
  openstaand: number | null;
}

interface FactuurPage {
  items: FactuurListItem[];
  total: number;
  page: number;
  size: number;
}

/** Server page size — kept in sync with the api default (FactuurListQueryService.DEFAULT_PAGE_SIZE). */
const PAGE_SIZE = 25;

/** Status badge tones — identical to the Relaties Facturen tile (relatieTiles FACTUUR_STATUS_TONE).
 *  OPENSTAAND = warning (orange), matching Jmix FactuurFormat.statusModifier. */
const FACTUUR_STATUS_TONE: Record<FactuurListItem["status"], StatusTone> = {
  CONCEPT: "neutral",
  BETAALD: "success",
  OPENSTAAND: "warning",
  VERVALLEN: "danger",
};

// invoices-migration Epic 6 — the standalone, UNIFIED invoice browse over the base `factuur` table
// (the dedicated left-menu entry point alongside the per-customer Relaties "Facturen" tile). A single
// list with a `factuurType` column + a status column — NOT per-type tabs (decided against the Jmix
// source). Search (number OR customer name), an optional invoice-date range, paging and row-open are
// all SERVER-side: `?q=&from=&to=&page=&size=` to `GET /api/facturen`, which returns
// `{ items, total, page, size }`. The url is RELATIVE — the dataProvider prepends `/api` once (a
// leading `/` would double to /api/api).
//
// NOTE (status is a column, not a filter): the payment status is DERIVED per row (api side), so it is
// deliberately not a server-side filter — filtering on a computed value would make the paged `total`
// disagree with the materialised rows. The real stored-column filters (date range / q) page correctly.
export function FactuurList({ onOpen }: { onOpen?: (id: string) => void } = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  // Debounce the search box; a new search resets to the first page so the user never lands out-of-range.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  // Changing a date bound is an immediate filter; reset to the first page too.
  useEffect(() => {
    setPage(1);
  }, [from, to]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    params.set("page", String(page));
    params.set("size", String(PAGE_SIZE));
    return `facturen?${params.toString()}`;
  }, [debounced, from, to, page]);

  const { data, isLoading, isError, refetch } = useCustom<FactuurPage>({ url, method: "get" });

  const rows = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toRow = Math.min(page * PAGE_SIZE, total);

  // Kolommen verschuiven en breder maken: wat je instelt blijft staan, per gebruiker.
  const { indeling, setIndeling } = useTabelIndeling("facturen");

  const columns: Column<FactuurListItem>[] = [
    { key: "nummer", header: t("factuurListView.column.nummer"), cell: (r) => r.nummer },
    {
      key: "datum",
      header: t("factuurListView.column.datum"),
      cell: (r) => formatDate(r.factuurDatum, locale),
    },
    {
      key: "status",
      header: t("factuurListView.column.status"),
      cell: (r) => (
        <StatusBadge tone={FACTUUR_STATUS_TONE[r.status]}>
          {t(`factuurListView.status.${r.status}`)}
        </StatusBadge>
      ),
    },
    { key: "klant", header: t("factuurListView.column.klant"), cell: (r) => r.klantNaam ?? "—" },
    {
      key: "totaal",
      header: t("factuurListView.column.totaal"),
      cell: (r) => formatEuro(r.totaal, locale),
    },
    {
      key: "openstaand",
      header: t("factuurListView.column.openstaand"),
      cell: (r) => formatEuro(r.openstaand, locale),
    },
    {
      key: "type",
      header: t("factuurListView.column.type"),
      cell: (r) =>
        r.factuurType ? t(`factuurListView.type.${r.factuurType}`, undefined, r.factuurType) : "—",
    },
  ];

  if (onOpen) {
    columns.push({
      // De openknop hoort bij de rij en niet bij de gegevens: die blijft rechts staan.
      key: "acties",
      vast: true,
      header: "",
      cell: (r) => (
        <Button variant="secondary" onClick={() => onOpen(String(r.id))}>
          {t("common.open")}
        </Button>
      ),
    });
  }

  return (
    <FormCard>
      <SectionHeader>{t("factuurListView.title")}</SectionHeader>
      <div className="fawkes-products-toolbar">
        <div className="fawkes-products-search">
          <SearchIcon className="fawkes-products-search__icon" width={16} height={16} />
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={t("factuurListView.search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t("factuurListView.search.placeholder")}
          />
        </div>
        <div className="fawkes-products-filter">
          <label htmlFor="factuur-from">{t("factuurListView.filter.from")}</label>
          <DateField id="factuur-from"
            className="fui-field__input"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="fawkes-products-filter">
          <label htmlFor="factuur-to">{t("factuurListView.filter.to")}</label>
          <DateField id="factuur-to"
            className="fui-field__input"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <div className="fawkes-products-toolbar__spacer" />
        <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
          {t("factuurListView.refresh")}
        </Button>
      </div>
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("factuurListView.loadFailed")}</p>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            getRowKey={(r) => String(r.id)}
            empty={t("factuurListView.empty")}
            indeling={indeling}
            onIndelingChange={setIndeling}
          />
          <TablePager
            page={page}
            pageCount={pageCount}
            total={total}
            from={fromRow}
            to={toRow}
            pageSize={PAGE_SIZE}
            onPage={setPage}
            labels={{
              range: t("factuurListView.pagination.range"),
              previous: t("factuurListView.pagination.previous"),
              next: t("factuurListView.pagination.next"),
              pageSize: t("pager.pageSize"),
              page: t("pager.page"),
            }}
          />
        </>
      )}
    </FormCard>
  );
}
